import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, Link, useParams } from 'react-router-dom';
import { BookOpenIcon, HeartHandshakeIcon, UserIcon, UsersIcon, FlameIcon, HeartIcon, MapPinIcon, AlertCircleIcon, ListIcon } from 'lucide-react';
import BooksGrid from '../components/Books/BooksGrid';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { FilterSidebar, FilterSidebarWrapper, NavItem, FilterSection } from '../components/UI/FilterSidebar';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
// Mock book data with tags
const mockBooks = [{
  id: '1',
  title: 'The Silent Echo',
  author: 'Elena Richards',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Mystery Thriller',
  tags: ['enemies-to-lovers', 'rivals-to-lovers', 'strong-female-lead', 'suspense', 'plot-twists', 'small-town', 'slow-burn-trope'],
  spiceLevel: 3,
  darknessLevel: 4,
  rating: 4.5,
  releaseDate: '2022-03-15',
  series: 'Echo Series',
  seriesNumber: 1,
  formats: ['ebook', 'paperback', 'audiobook'],
  genres: ['Mystery', 'Thriller'],
  heatLevel: 3,
  characterRelationships: [{
    type: 'Enemies to Lovers',
    style: ['Slow Burn']
  }],
  reviewCount: 120
}, {
  id: '2',
  title: 'Heart of the Storm',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1516979187457-637abb4f9353?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Fantasy Romance',
  tags: ['enemies-to-lovers', 'magic', 'slow-burn', 'slow-burn-trope', 'found-family', 'redemption', 'fantasy-world', 'chosen-one', 'bipoc'],
  spiceLevel: 4,
  darknessLevel: 3,
  rating: 4.7,
  releaseDate: '2022-05-22',
  series: 'Elements Trilogy',
  seriesNumber: 1,
  formats: ['ebook', 'hardcover', 'audiobook'],
  genres: ['Fantasy', 'Romance'],
  heatLevel: 4,
  characterRelationships: [{
    type: 'Enemies to Lovers',
    style: ['Slow Burn']
  }],
  reviewCount: 145
}, {
  id: '3',
  title: 'Whispers in the Dark',
  author: 'Sarah Johnson',
  coverImage: 'https://images.unsplash.com/photo-1598646506899-ac6be1060f4b?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Horror',
  tags: ['violence', 'small-town', 'paranormal', 'coming-of-age', 'grief-loss', 'found-identity'],
  spiceLevel: 1,
  darknessLevel: 5,
  rating: 4.2,
  releaseDate: '2021-10-31',
  formats: ['paperback', 'ebook'],
  genres: ['Horror', 'Supernatural'],
  heatLevel: 1,
  reviewCount: 78
}, {
  id: '4',
  title: 'Midnight Shadows',
  author: 'Sarah Johnson',
  coverImage: 'https://images.unsplash.com/photo-1515876305430-f06edab8282a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Supernatural Thriller',
  tags: ['small-town', 'redemption', 'violence', 'paranormal', 'forced-proximity', 'grief-loss'],
  spiceLevel: 2,
  darknessLevel: 4,
  rating: 4.0,
  releaseDate: '2022-01-15',
  formats: ['hardcover', 'ebook', 'audiobook'],
  genres: ['Supernatural', 'Thriller'],
  heatLevel: 2,
  characterRelationships: [{
    type: 'Forced Proximity',
    style: ['Slow Burn']
  }],
  reviewCount: 63
}, {
  id: '5',
  title: 'Soul of Fire',
  author: 'Marcus Chen',
  coverImage: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Fantasy Romance',
  tags: ['slow-burn', 'slow-burn-trope', 'magic', 'redemption', 'found-family', 'fantasy-world', 'bipoc'],
  spiceLevel: 4,
  darknessLevel: 3,
  rating: 4.8,
  releaseDate: '2022-07-10',
  series: 'Elements Trilogy',
  seriesNumber: 2,
  formats: ['ebook', 'hardcover', 'audiobook'],
  genres: ['Fantasy', 'Romance'],
  heatLevel: 4,
  characterRelationships: [{
    type: 'Forbidden Love',
    style: ['Slow Burn']
  }],
  reviewCount: 112
}, {
  id: '6',
  title: 'Broken Promises',
  author: 'Jessica Martinez',
  coverImage: 'https://images.unsplash.com/photo-1589998059171-988d887df646?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Contemporary Romance',
  tags: ['friends-to-lovers', 'strong-female-lead', 'small-town', 'coming-of-age', 'fake-dating', 'slow-burn-trope'],
  spiceLevel: 3,
  darknessLevel: 2,
  rating: 4.3,
  releaseDate: '2022-02-14',
  formats: ['paperback', 'ebook'],
  genres: ['Contemporary', 'Romance'],
  heatLevel: 3,
  characterRelationships: [{
    type: 'Friends to Lovers',
    style: ['Slow Burn']
  }],
  reviewCount: 89
}, {
  id: '7',
  title: 'The Last Guardian',
  author: 'Michael Torres',
  coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Urban Fantasy',
  tags: ['brooding-hero', 'anti-hero', 'found-family', 'lgbtq', 'magic', 'redemption', 'found-identity', 'bipoc'],
  spiceLevel: 2,
  darknessLevel: 3,
  rating: 4.6,
  releaseDate: '2021-11-05',
  formats: ['hardcover', 'ebook', 'audiobook'],
  genres: ['Urban Fantasy', 'Paranormal'],
  heatLevel: 2,
  characterRelationships: [{
    type: 'Slow Burn',
    style: ['Found Family']
  }],
  reviewCount: 94
}, {
  id: '8',
  title: 'Summer in Paris',
  author: 'Emily Watson',
  coverImage: 'https://images.unsplash.com/photo-1558901357-ca41e027e43a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Contemporary Romance',
  tags: ['big-city', 'friends-to-lovers', 'slow-burn', 'slow-burn-trope', 'coming-of-age', 'fake-dating'],
  spiceLevel: 3,
  darknessLevel: 1,
  rating: 4.4,
  releaseDate: '2022-06-01',
  formats: ['paperback', 'ebook'],
  genres: ['Contemporary', 'Romance'],
  heatLevel: 3,
  characterRelationships: [{
    type: 'Friends to Lovers',
    style: ['Slow Burn']
  }],
  reviewCount: 76
}, {
  id: '9',
  title: 'The Forgotten Kingdom',
  author: 'Alexander Wright',
  coverImage: 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Historical Fantasy',
  tags: ['enemies-to-lovers', 'rivals-to-lovers', 'brooding-hero', 'anti-hero', 'chosen-one', 'redemption', 'violence', 'fantasy-world'],
  spiceLevel: 2,
  darknessLevel: 4,
  rating: 4.7,
  releaseDate: '2021-09-20',
  series: 'Lost Kingdoms',
  seriesNumber: 1,
  formats: ['hardcover', 'ebook', 'audiobook'],
  genres: ['Historical', 'Fantasy'],
  heatLevel: 2,
  characterRelationships: [{
    type: 'Enemies to Lovers',
    style: ['Slow Burn']
  }],
  reviewCount: 103
}, {
  id: '10',
  title: 'Beneath the Surface',
  author: 'Rachel Kim',
  coverImage: 'https://images.unsplash.com/photo-1551029506-0807df4e2031?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Psychological Thriller',
  tags: ['strong-female-lead', 'neurodivergent', 'plot-twists', 'sexual-content', 'found-identity', 'bipoc'],
  spiceLevel: 2,
  darknessLevel: 5,
  rating: 4.9,
  releaseDate: '2022-04-05',
  formats: ['paperback', 'ebook', 'audiobook'],
  genres: ['Psychological', 'Thriller'],
  heatLevel: 2,
  characterRelationships: [{
    type: 'Slow Burn',
    style: ['Psychological']
  }],
  reviewCount: 132
}, {
  id: '11',
  title: 'Starlight Melody',
  author: 'David Chen',
  coverImage: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Sci-Fi Romance',
  tags: ['slow-burn', 'slow-burn-trope', 'lgbtq', 'found-family', 'neurodivergent', 'forced-proximity', 'bipoc'],
  spiceLevel: 3,
  darknessLevel: 2,
  rating: 4.5,
  releaseDate: '2022-08-12',
  formats: ['hardcover', 'ebook'],
  genres: ['Science Fiction', 'Romance'],
  heatLevel: 3,
  characterRelationships: [{
    type: 'Slow Burn',
    style: ['Found Family']
  }],
  reviewCount: 84
}, {
  id: '12',
  title: 'The Secret Garden',
  author: 'Frances Hodgson Burnett',
  coverImage: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Classic',
  tags: ['coming-of-age', 'small-town', 'redemption', 'found-identity', 'grief-loss'],
  spiceLevel: 0,
  darknessLevel: 1,
  rating: 4.8,
  releaseDate: '1911-01-01',
  formats: ['hardcover', 'paperback', 'ebook', 'audiobook'],
  genres: ['Classic', 'Literary Fiction'],
  heatLevel: 0,
  reviewCount: 215
}];
// Tag data
const tagData: Record<string, {
  id: string;
  name: string;
  description: string;
  count: number;
  category: string;
  color: string;
}> = {
  'enemies-to-lovers': {
    id: 'enemies-to-lovers',
    name: 'Enemies to Lovers',
    description: 'A romance trope where characters begin as enemies or rivals and gradually develop romantic feelings for each other.',
    count: 245,
    category: 'Tropes',
    color: 'bg-indigo-600'
  },
  'found-family': {
    id: 'found-family',
    name: 'Found Family',
    description: 'Characters who are not related by blood form deep familial bonds with each other.',
    count: 187,
    category: 'Tropes',
    color: 'bg-indigo-600'
  },
  'fake-dating': {
    id: 'fake-dating',
    name: 'Fake Dating',
    description: 'Characters pretend to be in a relationship but develop real feelings for each other along the way.',
    count: 210,
    category: 'Tropes',
    color: 'bg-indigo-600'
  },
  'forced-proximity': {
    id: 'forced-proximity',
    name: 'Forced Proximity',
    description: 'Characters are forced to spend time together in close quarters, leading to unexpected connection.',
    count: 198,
    category: 'Tropes',
    color: 'bg-indigo-600'
  },
  'slow-burn-trope': {
    id: 'slow-burn-trope',
    name: 'Slow Burn',
    description: 'Romantic tension builds slowly throughout the story before the characters finally get together.',
    count: 176,
    category: 'Tropes',
    color: 'bg-indigo-600'
  },
  'brooding-hero': {
    id: 'brooding-hero',
    name: 'Brooding Hero',
    description: 'A moody, introspective character who often has a troubled past or carries emotional burdens.',
    count: 156,
    category: 'Characters',
    color: 'bg-orange-600'
  },
  'strong-female-lead': {
    id: 'strong-female-lead',
    name: 'Strong Female Lead',
    description: 'A female protagonist who demonstrates independence, courage, and agency throughout the story.',
    count: 213,
    category: 'Characters',
    color: 'bg-orange-600'
  },
  'anti-hero': {
    id: 'anti-hero',
    name: 'Anti-Hero',
    description: 'A protagonist who lacks conventional heroic qualities, often morally ambiguous or flawed.',
    count: 189,
    category: 'Characters',
    color: 'bg-orange-600'
  },
  'chosen-one': {
    id: 'chosen-one',
    name: 'Chosen One',
    description: 'A character destined for greatness or to fulfill a prophecy that will change the world.',
    count: 167,
    category: 'Characters',
    color: 'bg-orange-600'
  },
  'slow-burn': {
    id: 'slow-burn',
    name: 'Slow Burn',
    description: 'A romantic relationship that develops gradually over time, with tension building slowly before the characters get together.',
    count: 178,
    category: 'Relationship Types',
    color: 'bg-pink-600'
  },
  'friends-to-lovers': {
    id: 'friends-to-lovers',
    name: 'Friends to Lovers',
    description: 'Characters begin as friends before developing romantic feelings for each other.',
    count: 142,
    category: 'Relationship Types',
    color: 'bg-pink-600'
  },
  'rivals-to-lovers': {
    id: 'rivals-to-lovers',
    name: 'Rivals to Lovers',
    description: 'Competing characters develop romantic feelings for each other through their rivalry.',
    count: 134,
    category: 'Relationship Types',
    color: 'bg-pink-600'
  },
  lgbtq: {
    id: 'lgbtq',
    name: 'LGBTQ+',
    description: 'Books featuring lesbian, gay, bisexual, transgender, and queer characters and themes.',
    count: 328,
    category: 'Representation',
    color: 'bg-purple-600'
  },
  neurodivergent: {
    id: 'neurodivergent',
    name: 'Neurodivergent',
    description: 'Books with characters who are on the autism spectrum, have ADHD, dyslexia, or other neurodivergent conditions.',
    count: 156,
    category: 'Representation',
    color: 'bg-purple-600'
  },
  bipoc: {
    id: 'bipoc',
    name: 'BIPOC',
    description: 'Books centering Black, Indigenous, and People of Color characters and experiences.',
    count: 245,
    category: 'Representation',
    color: 'bg-purple-600'
  },
  'coming-of-age': {
    id: 'coming-of-age',
    name: 'Coming of Age',
    description: 'Stories that focus on the psychological and moral growth of a protagonist from youth to adulthood.',
    count: 215,
    category: 'Themes',
    color: 'bg-blue-600'
  },
  redemption: {
    id: 'redemption',
    name: 'Redemption',
    description: 'Stories where characters seek atonement or redemption for past actions.',
    count: 178,
    category: 'Themes',
    color: 'bg-blue-600'
  },
  'found-identity': {
    id: 'found-identity',
    name: 'Found Identity',
    description: 'Characters discover who they truly are through the course of the story.',
    count: 143,
    category: 'Themes',
    color: 'bg-blue-600'
  },
  'small-town': {
    id: 'small-town',
    name: 'Small Town',
    description: 'Stories set in small, close-knit communities where everyone knows each other.',
    count: 183,
    category: 'Settings',
    color: 'bg-green-600'
  },
  'big-city': {
    id: 'big-city',
    name: 'Big City',
    description: 'Stories set in large metropolitan areas with diverse populations and urban environments.',
    count: 167,
    category: 'Settings',
    color: 'bg-green-600'
  },
  'fantasy-world': {
    id: 'fantasy-world',
    name: 'Fantasy World',
    description: 'Stories set in fully imagined fantasy realms with their own rules, magic, and history.',
    count: 221,
    category: 'Settings',
    color: 'bg-green-600'
  },
  violence: {
    id: 'violence',
    name: 'Violence',
    description: 'Books containing scenes of violence, fighting, or physical conflict.',
    count: 278,
    category: 'Content Warnings',
    color: 'bg-amber-600'
  },
  'sexual-content': {
    id: 'sexual-content',
    name: 'Sexual Content',
    description: 'Books containing explicit sexual scenes or references.',
    count: 312,
    category: 'Content Warnings',
    color: 'bg-amber-600'
  },
  'grief-loss': {
    id: 'grief-loss',
    name: 'Grief & Loss',
    description: 'Books dealing with death, grief, or significant loss of a person or relationship.',
    count: 198,
    category: 'Content Warnings',
    color: 'bg-amber-600'
  },
  magic: {
    id: 'magic',
    name: 'Magic',
    description: 'Stories featuring magical elements, systems, or powers.',
    count: 245,
    category: 'Themes',
    color: 'bg-blue-600'
  },
  'plot-twists': {
    id: 'plot-twists',
    name: 'Plot Twists',
    description: 'Books with surprising revelations or unexpected turns in the storyline.',
    count: 189,
    category: 'Themes',
    color: 'bg-blue-600'
  },
  paranormal: {
    id: 'paranormal',
    name: 'Paranormal',
    description: 'Stories involving supernatural elements like ghosts, vampires, or other paranormal phenomena.',
    count: 203,
    category: 'Themes',
    color: 'bg-blue-600'
  }
};
// Helper function to get color based on tag category
const getTagColor = (category: string) => {
  switch (category) {
    case 'Tropes':
      return 'bg-indigo-100 text-indigo-900 dark:bg-indigo-900/30 dark:text-indigo-300';
    case 'Characters':
      return 'bg-orange-100 text-orange-900 dark:bg-orange-900/30 dark:text-orange-300';
    case 'Relationship Types':
      return 'bg-pink-100 text-pink-900 dark:bg-pink-900/30 dark:text-pink-300';
    case 'Representation':
      return 'bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-300';
    case 'Themes':
      return 'bg-blue-100 text-blue-900 dark:bg-blue-900/30 dark:text-blue-300';
    case 'Settings':
      return 'bg-green-100 text-green-900 dark:bg-green-900/30 dark:text-green-300';
    case 'Content Warnings':
      return 'bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-300';
    default:
      return 'bg-gray-100 text-gray-900 dark:bg-gray-800 dark:text-gray-300';
  }
};
// Map category to page type for icon
const getCategoryPageType = (category: string): string => {
  switch (category) {
    case 'Tropes':
      return 'tropes';
    case 'Characters':
      return 'characters';
    case 'Relationship Types':
      return 'relationshiptypes';
    case 'Representation':
      return 'representation';
    case 'Themes':
      return 'themes';
    case 'Settings':
      return 'settings';
    case 'Content Warnings':
      return 'contentwarnings';
    default:
      return 'viewall';
  }
};
// Map category to href
const getCategoryHref = (category: string): string => {
  switch (category) {
    case 'Tropes':
      return '/tropes';
    case 'Characters':
      return '/characters';
    case 'Relationship Types':
      return '/relationship-types';
    case 'Representation':
      return '/representation';
    case 'Themes':
      return '/themes';
    case 'Settings':
      return '/settings';
    case 'Content Warnings':
      return '/content-warnings';
    default:
      return '/tagged-books';
  }
};
const TaggedBooks = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    tagId: pathTagId
  } = useParams();
  const searchParams = new URLSearchParams(location.search);
  const queryTagId = searchParams.get('tag');
  const tagId = pathTagId || queryTagId;
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [minSpiceLevel, setMinSpiceLevel] = useState(0);
  const [maxSpiceLevel, setMaxSpiceLevel] = useState(5);
  const [minDarknessLevel, setMinDarknessLevel] = useState(0);
  const [maxDarknessLevel, setMaxDarknessLevel] = useState(5);
  const [selectedGenres, setSelectedGenres] = useState<string[]>([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Get tag info
  const tagInfo = tagId ? tagData[tagId] : null;
  // If no tag is provided, redirect to all tags page
  useEffect(() => {
    if (!tagId) {
      navigate('/tagged-books');
    }
  }, [tagId, navigate]);
  // Filter books by tag and other filters
  const filteredBooks = mockBooks.filter(book => {
    if (tagId && !book.tags.includes(tagId)) return false;
    if (searchTerm && !book.title.toLowerCase().includes(searchTerm.toLowerCase()) && !book.author.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (book.spiceLevel < minSpiceLevel || book.spiceLevel > maxSpiceLevel) return false;
    if (book.darknessLevel < minDarknessLevel || book.darknessLevel > maxDarknessLevel) return false;
    if (selectedGenres.length > 0 && !selectedGenres.includes(book.genre)) return false;
    return true;
  });
  // Sort books
  const sortedBooks = [...filteredBooks].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'author':
        return a.author.localeCompare(b.author);
      case 'newest':
        return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
      case 'oldest':
        return new Date(a.releaseDate).getTime() - new Date(b.releaseDate).getTime();
      case 'rating':
      default:
        return b.rating - a.rating;
    }
  });
  // Group books by series
  const seriesBooks = useMemo(() => {
    const seriesMap: Record<string, typeof mockBooks> = {};
    sortedBooks.forEach(book => {
      if (book.series) {
        if (!seriesMap[book.series]) seriesMap[book.series] = [];
        seriesMap[book.series].push(book);
      }
    });
    Object.values(seriesMap).forEach(books => {
      books.sort((a, b) => (a.seriesNumber || 0) - (b.seriesNumber || 0));
    });
    return seriesMap;
  }, [sortedBooks]);
  // Get standalone books
  const standaloneBooks = useMemo(() => {
    return sortedBooks.filter(book => !book.series);
  }, [sortedBooks]);
  // Get unique genres
  const genres = [...new Set(mockBooks.map(book => book.genre))].sort();
  // Map book to card props
  const mapBookToCardProps = (book: (typeof mockBooks)[0]) => ({
    id: book.id,
    title: book.title,
    author: book.author,
    coverImage: book.coverImage,
    rating: book.rating,
    reviewCount: book.reviewCount,
    genres: book.genres || [book.genre],
    subgenres: [],
    spiceLevel: book.spiceLevel || book.heatLevel,
    darknessLevel: book.darknessLevel,
    endingType: book.genres?.includes('Romance') ? 'HEA' : null,
    relationshipType: book.characterRelationships?.[0]?.type,
    contentWarnings: book.tags?.filter(tag => ['violence', 'sexual-content'].includes(tag)),
    seriesName: book.series,
    seriesStatus: book.series ? 'ongoing' : null,
    seriesNumber: book.seriesNumber,
    totalSeriesBooks: book.series ? 3 : null,
    formats: book.formats
  });
  // Navigation items
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    isActive: tagInfo?.category === 'Tropes',
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    isActive: tagInfo?.category === 'Characters',
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    isActive: tagInfo?.category === 'Relationship Types',
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Representation',
    href: '/representation',
    icon: HeartIcon,
    isActive: tagInfo?.category === 'Representation'
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon,
    isActive: tagInfo?.category === 'Themes'
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    isActive: tagInfo?.category === 'Settings',
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon,
    isActive: tagInfo?.category === 'Content Warnings'
  }];
  // Filter sections
  const filterSections: FilterSection[] = [{
    id: 'sort',
    title: 'Sort By',
    type: 'radio',
    options: [{
      value: 'rating',
      label: 'Top Rated'
    }, {
      value: 'newest',
      label: 'Newest'
    }, {
      value: 'oldest',
      label: 'Oldest'
    }, {
      value: 'title',
      label: 'Title'
    }, {
      value: 'author',
      label: 'Author'
    }],
    value: sortBy,
    onChange: (val: string) => setSortBy(val)
  }, {
    id: 'genres',
    title: 'Genres',
    type: 'checkbox',
    options: genres.map(g => ({
      value: g,
      label: g
    })),
    value: selectedGenres,
    onChange: (val: string) => {
      setSelectedGenres(prev => prev.includes(val) ? prev.filter(g => g !== val) : [...prev, val]);
    }
  }];
  const handleReset = () => {
    setSortBy('rating');
    setMinSpiceLevel(0);
    setMaxSpiceLevel(5);
    setMinDarknessLevel(0);
    setMaxDarknessLevel(5);
    setSelectedGenres([]);
    setSearchTerm('');
  };
  if (!tagInfo) {
    return <div className={`min-h-screen flex items-center justify-center ${dk.page}`} data-id="element-14313" data-mp="33kzjxlmik870">
        <div className="text-center" data-id="element-14314" data-mp="38klx5v2cdoft">
          <h1 className={`text-2xl font-bold ${dk.textHeading}`} data-id="element-14315" data-mp="38lnf8jhrzb71">
            Tag not found
          </h1>
          <p className={`mt-2 ${dk.textSecondary}`} data-id="element-14316" data-mp="3ckcw4m20vavf">
            The tag you're looking for doesn't exist or isn't available.
          </p>
          <Link to="/tagged-books" className={`mt-4 inline-block px-4 py-2 rounded-md ${rt.primaryButton}`} data-id="element-14317" data-mp="1i7wjn3imv0i4">
            View All Tags
          </Link>
        </div>
      </div>;
  }
  return <div className={`min-h-screen ${dk.page}`} data-id="element-14318" data-mp="ucb9lnkk1jbd">
      {/* Content Page Header */}
      <ContentPageHeader title={tagInfo.name} icon={getContentPageIcon(getCategoryPageType(tagInfo.category))} searchPlaceholder="Search books..." searchQuery={searchTerm} setSearchQuery={setSearchTerm} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-14319" data-mp="183g3sa55ljeb" />

      {/* Tag Info Banner */}
      <div className={`border-b ${dk.surface} ${dk.border}`} data-id="element-14320" data-mp="bgl5nut2acuu">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4" data-id="element-14321" data-mp="pfky7djp3bwa">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" data-id="element-14322" data-mp="kenf8yqv2dtq">
            <div className="flex items-center gap-3" data-id="element-14323" data-mp="20sdnmhp0jo96">
              <Link to={getCategoryHref(tagInfo.category)} className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getTagColor(tagInfo.category)}`} data-id="element-14324" data-mp="20v9i1w1njusv">
                {tagInfo.category}
              </Link>
              <p className={`text-sm ${dk.textSecondary}`} data-id="element-14325" data-mp="28e9283wxtqda">
                {tagInfo.description}
              </p>
            </div>
            <div className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-14326" data-mp="1zbzt9ihbi520">
              {filteredBooks.length} books
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-14327" data-mp="2s0oy9slhuuj1">
        <div className="flex gap-8" data-id="element-14328" data-mp="2rzvnmgjezkvm">
          {/* Sidebar */}
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-14329" data-mp="2dckbacferwug">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={filterSections} onReset={handleReset} searchable={false} data-id="element-14330" data-mp="35yd6o0ydk977" />
          </FilterSidebarWrapper>

          {/* Main Content Area */}
          <main className="flex-1 min-w-0" data-id="element-14331" data-mp="lj1ky6cku32v">
            {/* Spice & Darkness Level Filters */}
            <div className={`mb-6 p-4 rounded-lg border ${dk.card}`} data-id="element-14332" data-mp="qce626i1rrqv">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14333" data-mp="v9ntxhpsfvcl">
                {/* Spice Level */}
                <div data-id="element-14334" data-mp="3ttmlkjpayfif">
                  <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-14335" data-mp="3tu5s749i5hh5">
                    Spice Level
                  </h3>
                  <div className="flex items-center gap-2" data-id="element-14336" data-mp="7kbmama3zvce">
                    <select value={minSpiceLevel} onChange={e => setMinSpiceLevel(parseInt(e.target.value))} className={`flex-1 px-3 py-1.5 text-sm rounded-md border focus:ring-indigo-500 focus:border-indigo-500 ${dk.input}`} data-id="element-14337" data-mp="bmgf5isqgnxz">
                      {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n} data-id="element-14338" data-mp="33i2edb101924">
                          Min: {n === 0 ? 'None' : n}
                        </option>)}
                    </select>
                    <span className={dk.textMuted} data-id="element-14339" data-mp="2tt0wffaw8hli">to</span>
                    <select value={maxSpiceLevel} onChange={e => setMaxSpiceLevel(parseInt(e.target.value))} className={`flex-1 px-3 py-1.5 text-sm rounded-md border focus:ring-indigo-500 focus:border-indigo-500 ${dk.input}`} data-id="element-14340" data-mp="2tqeq1izqo84v">
                      {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n} data-id="element-14341" data-mp="e13d06a1n01r">
                          Max: {n === 0 ? 'None' : n}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Darkness Level */}
                <div data-id="element-14342" data-mp="s4sl202x06z0">
                  <h3 className={`text-sm font-medium mb-2 ${dk.textSecondary}`} data-id="element-14343" data-mp="wxdbcrmm41xs">
                    Darkness Level
                  </h3>
                  <div className="flex items-center gap-2" data-id="element-14344" data-mp="j4dx0sykgdrt">
                    <select value={minDarknessLevel} onChange={e => setMinDarknessLevel(parseInt(e.target.value))} className={`flex-1 px-3 py-1.5 text-sm rounded-md border focus:ring-indigo-500 focus:border-indigo-500 ${dk.input}`} data-id="element-14345" data-mp="o639md1fszi0">
                      {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n} data-id="element-14346" data-mp="16xw3sas8sqrb">
                          Min: {n === 0 ? 'None' : n}
                        </option>)}
                    </select>
                    <span className={dk.textMuted} data-id="element-14347" data-mp="3rnbqatkfhtb5">to</span>
                    <select value={maxDarknessLevel} onChange={e => setMaxDarknessLevel(parseInt(e.target.value))} className={`flex-1 px-3 py-1.5 text-sm rounded-md border focus:ring-indigo-500 focus:border-indigo-500 ${dk.input}`} data-id="element-14348" data-mp="3k1q1ouxjtycq">
                      {[0, 1, 2, 3, 4, 5].map(n => <option key={n} value={n} data-id="element-14349" data-mp="30f1sdd8yjmv9">
                          Max: {n === 0 ? 'None' : n}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>

            {/* Books Grid */}
            {sortedBooks.length === 0 ? <div className="text-center py-12" data-id="element-14350" data-mp="2ho0uksjkpxda">
                <BookOpenIcon className={`mx-auto h-12 w-12 ${dk.textMuted}`} data-id="element-14351" data-mp="2hmppmpee6jkl" />
                <h3 className={`mt-2 text-sm font-medium ${dk.textHeading}`} data-id="element-14352" data-mp="2cnwdb0aucym0">
                  No books found
                </h3>
                <p className={`mt-1 text-sm ${dk.textSecondary}`} data-id="element-14353" data-mp="2qdhm2ddejz22">
                  Try adjusting your filters or search term.
                </p>
              </div> : <BooksGrid books={standaloneBooks.map(mapBookToCardProps)} seriesList={Object.entries(seriesBooks).map(([seriesName, books]) => ({
            id: books[0].id,
            name: seriesName,
            status: 'ongoing',
            books: books.map(mapBookToCardProps),
            totalBooks: books.length
          }))} columns={4} viewMode="grid" data-id="element-14354" data-mp="3092jrzbfud60" />}
          </main>
        </div>
      </div>
    </div>;
};
export default TaggedBooks;